import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Server, Database, Cloud, Radio, Cpu, Network, Smartphone, Globe2, Boxes } from "lucide-react";

const ICONS = {
  Server: Server, Database: Database, Kubernetes: Cloud, IoT: Radio,
  API: Globe2, "SCADA/OT": Cpu, "AI/LLM": Cpu, Network: Network,
  ERP: Boxes, Mobile: Smartphone,
};

const AssetDiscovery = () => {
  const [assets, setAssets] = useState([]);
  const [topology, setTopology] = useState({ nodes: [], edges: [] });
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/assets").then(({ data }) => setAssets(data));
    api.get("/assets/topology").then(({ data }) => setTopology(data));
  }, []);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return assets.filter((a) =>
      !q || a.name.toLowerCase().includes(q) || a.ip.toLowerCase().includes(q) || a.type.toLowerCase().includes(q)
    );
  }, [assets, filter]);

  // simple radial layout for topology
  const nodes = topology.nodes.map((n, i) => {
    const angle = (i / Math.max(1, topology.nodes.length)) * Math.PI * 2;
    const r = 130;
    return { ...n, x: 200 + Math.cos(angle) * r, y: 180 + Math.sin(angle) * r };
  });
  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Platform / Inventory"
        title="Asset Discovery"
        subtitle="Real-time inventory across network, cloud, container, IoT, OT, API, mobile and AI surfaces."
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 csx-card p-4">
          <input
            data-testid="asset-search"
            className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono outline-none focus:border-cyan-500/70 mb-3"
            placeholder="filter by name / ip / type..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
                  <th className="py-2 pr-2">Asset</th>
                  <th className="pr-2">Type</th>
                  <th className="pr-2">IP / Endpoint</th>
                  <th className="pr-2">Criticality</th>
                  <th>Exposure</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const Icon = ICONS[a.type] || Server;
                  const cCol = a.criticality === "critical" ? "text-rose-300" : a.criticality === "high" ? "text-amber-300" : "text-slate-300";
                  return (
                    <tr key={a.id} data-testid={`asset-row-${a.name}`} className="border-b border-slate-900 hover:bg-slate-900/40">
                      <td className="py-2 pr-2">
                        <div className="flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-cyan-400" />
                          <span className="text-slate-100">{a.name}</span>
                        </div>
                      </td>
                      <td className="pr-2 text-slate-400">{a.type}</td>
                      <td className="pr-2 font-mono text-slate-400">{a.ip}</td>
                      <td className={`pr-2 uppercase tracking-widest text-[10px] ${cCol}`}>{a.criticality}</td>
                      <td className="text-slate-400">{a.exposure}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10px] text-slate-500 uppercase tracking-widest">
            {filtered.length} / {assets.length} assets
          </div>
        </div>

        <div className="lg:col-span-2 csx-card p-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">Network Topology</div>
          <svg viewBox="0 0 400 360" className="w-full h-[360px]">
            <defs>
              <radialGradient id="bg" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(6,182,212,0.08)" />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
            </defs>
            <rect width="400" height="360" fill="url(#bg)" />
            {/* edges */}
            {topology.edges.map((e) => {
              const a = nodeMap[e.from], b = nodeMap[e.to];
              if (!a || !b) return null;
              return <line key={`${e.from}-${e.to}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="rgba(6,182,212,0.25)" strokeWidth="1" strokeDasharray="2 3" />;
            })}
            {/* center hub */}
            <circle cx="200" cy="180" r="6" fill="#d946ef" />
            <circle cx="200" cy="180" r="14" fill="none" stroke="#d946ef" strokeWidth="1" opacity="0.5" />
            {/* nodes */}
            {nodes.map((n) => {
              const fill = n.criticality === "critical" ? "#f43f5e" : n.criticality === "high" ? "#f59e0b" : "#06b6d4";
              return (
                <g key={n.id}>
                  <line x1="200" y1="180" x2={n.x} y2={n.y} stroke="rgba(148,163,184,0.15)" />
                  <circle cx={n.x} cy={n.y} r="5" fill={fill} />
                  <circle cx={n.x} cy={n.y} r="9" fill="none" stroke={fill} strokeOpacity="0.4" />
                  <text x={n.x} y={n.y - 12} textAnchor="middle" fontSize="9" fill="#94a3b8" fontFamily="JetBrains Mono">
                    {n.label.length > 14 ? n.label.slice(0, 14) + "…" : n.label}
                  </text>
                </g>
              );
            })}
          </svg>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-2">
            <span className="inline-block w-2 h-2 bg-rose-500 mr-1.5" /> Critical
            <span className="inline-block w-2 h-2 bg-amber-500 ml-3 mr-1.5" /> High
            <span className="inline-block w-2 h-2 bg-cyan-500 ml-3 mr-1.5" /> Standard
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDiscovery;
